import {
  annotationsOf,
  isHiddenMetaPath,
  isKeepPath,
  sameAnnotation,
  type RepositoryFile,
} from '@trove/core';

export type ChangeStatus = 'added' | 'modified' | 'deleted';

export type ChangedPath = {
  path: string;
  status: ChangeStatus;
};

export type PendingChangeStatus = ChangeStatus | 'annotated';

export type PendingChange = {
  path: string;
  status: PendingChangeStatus;
};

function fingerprint(files: RepositoryFile[] | undefined, path: string) {
  const file = files?.find((entry) => entry.path === path);
  if (!file) return undefined;
  if (file.contentHash) return `hash:${file.contentHash}`;
  if (file.blobOpaqueId) return `blob:${file.blobOpaqueId}`;
  if (file.content !== undefined) return `content:${file.content}`;
  return `path:${file.path}:${file.size}:${file.modifiedAt}`;
}

function keepFolderPath(path: string) {
  return path.endsWith('/.keep') ? path.slice(0, -'/.keep'.length) : path === '.keep' ? '' : null;
}

/** Paths whose content differs between two snapshots. Meta (`.trv`, `.keep`) is omitted. */
export function changedPaths(after: RepositoryFile[], before?: RepositoryFile[]): ChangedPath[] {
  const paths = new Set([
    ...after.map((file) => file.path),
    ...(before?.map((file) => file.path) ?? []),
  ]);

  const changes: ChangedPath[] = [];
  for (const path of [...paths].sort((a, b) => a.localeCompare(b))) {
    if (isHiddenMetaPath(path) || isKeepPath(path)) continue;
    const next = fingerprint(after, path);
    const prev = fingerprint(before, path);
    if (next === prev) continue;
    if (prev === undefined) changes.push({ path, status: 'added' });
    else if (next === undefined) changes.push({ path, status: 'deleted' });
    else changes.push({ path, status: 'modified' });
  }
  return changes;
}

/** Queued local edits vs the last committed baseline, including annotation-only and empty folders. */
export function pendingChanges(after: RepositoryFile[], before: RepositoryFile[]): PendingChange[] {
  const content = changedPaths(after, before);
  const byPath = new Map(content.map((change) => [change.path, change as PendingChange]));

  const paths = new Set([...after.map((file) => file.path), ...before.map((file) => file.path)]);
  for (const path of paths) {
    if (!isKeepPath(path)) continue;
    const folder = keepFolderPath(path);
    if (folder === null || folder === '' || byPath.has(folder)) continue;
    const next = fingerprint(after, path);
    const prev = fingerprint(before, path);
    if (prev === undefined && next !== undefined)
      byPath.set(folder, { path: folder, status: 'added' });
    else if (prev !== undefined && next === undefined)
      byPath.set(folder, { path: folder, status: 'deleted' });
  }

  const nextAnnotations = annotationsOf({ files: after }) ?? {};
  const prevAnnotations = annotationsOf({ files: before }) ?? {};
  for (const path of new Set([...Object.keys(nextAnnotations), ...Object.keys(prevAnnotations)])) {
    if (byPath.has(path)) continue;
    if (sameAnnotation(nextAnnotations[path], prevAnnotations[path])) continue;
    byPath.set(path, { path, status: 'annotated' });
  }

  return [...byPath.values()].sort((a, b) => a.path.localeCompare(b.path));
}

export function pendingChangeLabel(status: PendingChangeStatus) {
  if (status === 'added') return 'added';
  if (status === 'deleted') return 'deleted';
  if (status === 'annotated') return 'annotated';
  return 'edited';
}
